/**
 * **Where an attendee list seats someone** (docs/notes/2026-09-29-event-seating-spec.md).
 *
 * An attendee-list import may map its seating columns (an Eventbrite export's
 * Section, Row and Seat, a gala's Table) to the parts below. They are kept on
 * the attendee's node beside the list's employer and city, under
 * {@link SEAT_DETAIL_KEYS}, and never reach the scan: a seat names nobody.
 *
 * The seat is what the list said, not where anyone sat.
 */

export const SEAT_PARTS = ['section', 'table', 'row', 'seat', 'whole'] as const;
export type SeatPart = (typeof SEAT_PARTS)[number];

/**
 * The node detail each part is kept under. `whole` is a cell holding the full
 * assignment ("Sec 104 Row F Seat 7"), kept as the list wrote it.
 */
export const SEAT_DETAIL_KEYS: Readonly<Record<SeatPart, string>> = {
  section: 'seat_section',
  table: 'seat_table',
  row: 'seat_row',
  seat: 'seat_number',
  whole: 'seat_text',
};

const SEAT_KEY_SET: ReadonlySet<string> = new Set(Object.values(SEAT_DETAIL_KEYS));

/** Whether a node detail is part of a seat. */
export const isSeatDetailKey = (key: string): boolean => SEAT_KEY_SET.has(key);

const PART_WORDS: Readonly<Record<Exclude<SeatPart, 'whole'>, string>> = {
  section: 'Section',
  table: 'Table',
  row: 'Row',
  seat: 'Seat',
};

/**
 * A bare code ("104", "F", "12B", "AA") takes the part's word before it; a
 * value in words ("Balcony", "Table 12", "Orchestra Left") is shown as written.
 */
const BARE_CODE = /^(?:[A-Za-z]{0,2}\d+[A-Za-z]?|[A-Za-z]{1,2})$/;

function partText(part: Exclude<SeatPart, 'whole'>, value: string): string {
  return BARE_CODE.test(value) ? `${PART_WORDS[part]} ${value}` : value;
}

function detail(details: Readonly<Record<string, string>>, part: SeatPart): string | null {
  return details[SEAT_DETAIL_KEYS[part]]?.trim() || null;
}

/** The parts above the seat itself: where people sit together. */
const AREA_PARTS = ['section', 'table', 'row'] as const;

/**
 * **Where people sit together**: the section, table and row, without the seat
 * number ("Section 104, Row F", "Table 12"). Null when the list gives none of
 * them, including a seat written only as a whole cell, which is not read apart.
 */
export function seatArea(details: Readonly<Record<string, string>>): string | null {
  const parts = AREA_PARTS.flatMap((part) => {
    const value = detail(details, part);
    return value ? [partText(part, value)] : [];
  });
  return parts.length ? parts.join(', ') : null;
}

/**
 * **The seat in one line**: "Section 104, Row F, Seat 7", "Table 12, Seat 3",
 * or the list's whole cell. Null when the list seats them nowhere.
 */
export function seatLabel(details: Readonly<Record<string, string>>): string | null {
  const area = seatArea(details);
  const number = detail(details, 'seat');
  const whole = detail(details, 'whole');
  const parts = [area, number ? partText('seat', number) : null, whole].filter(
    (part): part is string => Boolean(part),
  );
  return parts.length ? parts.join(', ') : null;
}
